import React, { useRef, useState } from "react";
import toast from "react-hot-toast";
import swal from "sweetalert";
import {
  HiOutlineDocumentText,
  HiOutlineExternalLink,
  HiOutlineUpload,
  HiOutlineExclamationCircle,
  HiOutlineTrash,
} from "react-icons/hi";

const fileNameOf = (url) => {
  try {
    return decodeURIComponent(new URL(url).pathname.split("/").pop() || "Document");
  } catch (e) {
    return url.split("/").pop() || "Document";
  }
};

// A link built from the backend's development default can't be opened by anyone else.
const pointsAtLocalMachine = (url) => /^https?:\/\/(localhost|127\.0\.0\.1)(:|\/)/i.test(url || "");

const PermitDocumentPanel = ({ url, onUpload, onRemove }) => {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Check the file is really there before showing it, so a missing file gives a clear
  // message instead of a blank or error tab. The tab is opened up front so the browser
  // doesn't treat it as a pop-up.
  const openDocument = async (e) => {
    e.preventDefault();
    const tab = window.open("", "_blank");
    try {
      const res = await fetch(url, { method: "HEAD", cache: "no-store" });
      if (res.ok) {
        if (tab) {
          tab.opener = null;
          tab.location.href = url;
        } else {
          window.location.href = url;
        }
        return;
      }
      if (tab) tab.close();
      toast.error(
        `The server couldn't return this file (HTTP ${res.status}). It was saved, but the server can't find or serve it; please report this with the file name.`,
        { duration: 10000 }
      );
    } catch (err) {
      // The check itself can be blocked (e.g. by CORS); fall back to opening the link.
      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      } else {
        window.location.href = url;
      }
    }
  };

  const handleRemove = async () => {
    const confirmed = await swal({
      title: "Remove this document?",
      text: "The permit document will be deleted from this record.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!confirmed) return;
    setRemoving(true);
    const res = await onRemove();
    setRemoving(false);
    if (res && res.Successful) toast.success("Permit document removed");
    else toast.error((res && res.message) || "Couldn't remove the document", { duration: 8000 });
  };

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    const toastId = toast.loading("Uploading permit document...");
    const res = await onUpload(file);
    toast.dismiss(toastId);
    setUploading(false);
    if (res && res.Successful) toast.success("Permit document saved");
    else toast.error(`The permit document couldn't be uploaded${res && res.message ? `: ${res.message}` : ""}`, { duration: 8000 });
  };

  return (
    <div className="w-full p-4 mt-4 bg-white border rounded-lg border-slate-200">
      <p className="mb-3 text-gray-700 text-md">Permit Document</p>
      <div className="flex flex-wrap items-center gap-3">
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={openDocument}
            className="inline-flex items-center min-w-0 gap-2 px-3 py-2 text-sm font-medium border rounded-lg border-tclPrimary/30 bg-tclPrimary/5 text-tclPrimary hover:bg-tclPrimary/10"
          >
            <HiOutlineDocumentText className="w-5 h-5 shrink-0" />
            <span className="truncate max-w-[18rem]">{fileNameOf(url)}</span>
            <HiOutlineExternalLink className="w-4 h-4 shrink-0" />
          </a>
        ) : (
          <span className="text-sm text-slate-400">No permit document uploaded yet.</span>
        )}
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current && inputRef.current.click()}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border rounded-lg border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-60"
        >
          <HiOutlineUpload className="w-4 h-4" />
          {uploading ? "Uploading..." : url ? "Replace document" : "Attach document"}
        </button>
        {url && onRemove && (
          <button
            type="button"
            disabled={removing || uploading}
            onClick={handleRemove}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-60"
          >
            <HiOutlineTrash className="w-4 h-4" />
            {removing ? "Removing..." : "Remove document"}
          </button>
        )}
        <input ref={inputRef} type="file" className="hidden" onChange={handleFile} />
      </div>
      {pointsAtLocalMachine(url) && (
        <p className="flex items-start gap-1.5 mt-3 text-xs text-amber-700">
          <HiOutlineExclamationCircle className="w-4 h-4 shrink-0" />
          This link points at a development address ({new URL(url).host}), so it won't open for you. The backend's
          file address setting (RootStorageUri) needs fixing; the file itself is stored.
        </p>
      )}
    </div>
  );
};

export default PermitDocumentPanel;
